import { Schema } from "effect";
import {
  ConnectionIdSchema,
  RequestIdSchema,
  ThreadIdSchema,
  UserIdSchema,
  WorkspaceIdSchema,
} from "./ids";
import { HttpUrlSchema, IsoDateTimeSchema, PapersFlowBoundaryParseOptions } from "./standard";
import { WorkspaceRoleSchema } from "./workspace";

export const ConnectorToolkitKeySchema = Schema.Trim.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(100),
);
const ToolKeySchema = Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(180));
const ConnectorProviderSchema = Schema.Literal("composio");
const ConnectorProviderSessionIdSchema = Schema.Trim.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(256),
);
const ConnectorExternalConnectionIdSchema = Schema.Trim.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(256),
);
const ConnectorProviderUserIdSchema = Schema.Trim.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(300),
);
const ComposioWebhookIdentifierSchema = Schema.Trim.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(180),
);
const ComposioWebhookBoundaryParseOptions = {
  ...PapersFlowBoundaryParseOptions,
  // Composio owns this provider envelope and may add fields. PapersFlow retains only the
  // authenticated V3 metadata needed to resolve PapersFlow-owned connector state.
  onExcessProperty: "ignore",
} as const;

export const ConnectorScopeSchema = Schema.Literals(["personal", "workspace"]);
export const ConnectorStatusSchema = Schema.Literals(["connected", "pending", "expired", "error"]);
export const ToolRiskSchema = Schema.Literals([
  "read",
  "local_write",
  "external_write",
  "destructive",
  "billing",
  "membership",
  "native_execution",
]);

export const ConnectorToolkitSchema = Schema.Struct({
  key: ConnectorToolkitKeySchema,
  name: Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(160)),
  description: Schema.String.check(Schema.isMaxLength(1_000)),
  logoUrl: Schema.optionalKey(HttpUrlSchema),
  categories: Schema.Array(Schema.String.check(Schema.isMaxLength(80))).check(
    Schema.isMaxLength(20),
  ),
});

export const ConnectorConnectionSchema = Schema.Struct({
  id: ConnectionIdSchema,
  toolkit: ConnectorToolkitKeySchema,
  scope: ConnectorScopeSchema,
  workspaceId: WorkspaceIdSchema,
  ownerUserId: Schema.optionalKey(UserIdSchema),
  externalConnectionId: Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(256)),
  displayName: Schema.optionalKey(Schema.Trim.check(Schema.isMaxLength(160))),
  status: ConnectorStatusSchema,
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});

export const ConnectorToolSchema = Schema.Struct({
  key: ToolKeySchema,
  toolkit: ConnectorToolkitKeySchema,
  name: Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(180)),
  description: Schema.String.check(Schema.isMaxLength(2_000)),
  risk: ToolRiskSchema,
  inputJsonSchema: Schema.JsonObject,
});

export const ConnectorExecutionRequestSchema = Schema.Struct({
  connectionId: ConnectionIdSchema,
  toolKey: ToolKeySchema,
  input: Schema.JsonObject,
  idempotencyKey: Schema.Trim.check(Schema.isMinLength(8), Schema.isMaxLength(200)),
});

export const ConnectorExecutionResultSchema = Schema.Struct({
  executionId: Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(256)),
  status: Schema.Literals(["succeeded", "failed", "pending_approval"]),
  output: Schema.optionalKey(Schema.Json),
  safeSummary: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(4_000))),
  errorCode: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(120))),
});

export const ConnectorAuthorizationRequestSchema = Schema.Struct({
  workspaceId: WorkspaceIdSchema,
  threadId: Schema.optionalKey(ThreadIdSchema),
});

export const ConnectorAuthorizationResultSchema = Schema.Struct({
  toolkit: ConnectorToolkitKeySchema,
  provider: ConnectorProviderSchema,
  redirectUrl: HttpUrlSchema,
});

export const ConnectorSynchronizationResultSchema = Schema.Struct({
  provider: ConnectorProviderSchema,
  synchronized: Schema.Literal(true),
});

export const ResolveConnectorAuthorizationActorRequestSchema = Schema.Struct({
  requestId: RequestIdSchema,
  workspaceId: WorkspaceIdSchema,
  threadId: Schema.optionalKey(ThreadIdSchema),
});

export const ResolvedConnectorAuthorizationActorSchema = Schema.Struct({
  userId: UserIdSchema,
  workspaceId: WorkspaceIdSchema,
  role: WorkspaceRoleSchema,
  threadId: Schema.optionalKey(ThreadIdSchema),
});

export const ConnectorSessionPurposeSchema = Schema.Literals(["authorization", "agent"]);

export const ResolveConnectorSessionRequestSchema = Schema.Struct({
  requestId: RequestIdSchema,
  workspaceId: WorkspaceIdSchema,
  actorUserId: UserIdSchema,
  purpose: ConnectorSessionPurposeSchema,
  threadId: Schema.optionalKey(ThreadIdSchema),
});

export const ResolvedConnectorSessionSchema = Schema.Struct({
  sessionId: Schema.optionalKey(ConnectorProviderSessionIdSchema),
});

export const ConnectorProviderConnectionStatusSchema = Schema.Literals([
  "connected",
  "pending",
  "expired",
  "error",
  "disconnected",
]);

export const ConnectorProviderConnectionProjectionSchema = Schema.Struct({
  toolkit: ConnectorToolkitKeySchema,
  externalConnectionId: Schema.optionalKey(ConnectorExternalConnectionIdSchema),
  displayName: Schema.optionalKey(Schema.Trim.check(Schema.isMaxLength(160))),
  status: ConnectorProviderConnectionStatusSchema,
  capabilities: Schema.Array(
    Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(80)),
  ).check(Schema.isMaxLength(64)),
});

export const ComposioCalendarTriggerSlugs = [
  "GOOGLECALENDAR_GOOGLE_CALENDAR_EVENT_SYNC_TRIGGER",
  "OUTLOOK_EVENT_CHANGE_TRIGGER",
  "OUTLOOK_EVENT_TRIGGER",
] as const;

export const ComposioCalendarTriggerSlugSchema = Schema.Literals(ComposioCalendarTriggerSlugs);

export const ComposioCalendarTriggerProjectionSchema = Schema.Struct({
  triggerId: ComposioWebhookIdentifierSchema,
  triggerSlug: ComposioCalendarTriggerSlugSchema,
  externalConnectionId: ConnectorExternalConnectionIdSchema,
});

export const ComposioConnectionExpiredWebhookSchema = Schema.Struct({
  id: ComposioWebhookIdentifierSchema,
  timestamp: IsoDateTimeSchema,
  type: Schema.Literal("composio.connected_account.expired"),
  metadata: Schema.Struct({
    project_id: ComposioWebhookIdentifierSchema,
    org_id: ComposioWebhookIdentifierSchema,
  }),
  data: Schema.Struct({
    id: ConnectorExternalConnectionIdSchema,
    status: Schema.Literal("EXPIRED"),
    toolkit: Schema.Struct({
      slug: ConnectorToolkitKeySchema,
    }),
  }),
});

export const ComposioCalendarTriggerWebhookSchema = Schema.Struct({
  id: ComposioWebhookIdentifierSchema,
  timestamp: IsoDateTimeSchema,
  type: Schema.Literal("composio.trigger.message"),
  metadata: Schema.Struct({
    log_id: ComposioWebhookIdentifierSchema,
    trigger_slug: ComposioCalendarTriggerSlugSchema,
    trigger_id: ComposioWebhookIdentifierSchema,
    connected_account_id: ConnectorExternalConnectionIdSchema,
    auth_config_id: ComposioWebhookIdentifierSchema,
    user_id: ConnectorProviderUserIdSchema,
  }),
  data: Schema.JsonObject,
});

export const ComposioWebhookSchema = Schema.Union([
  ComposioConnectionExpiredWebhookSchema,
  ComposioCalendarTriggerWebhookSchema,
]);

export const ReconcileConnectorStateRequestSchema = Schema.Struct({
  requestId: RequestIdSchema,
  workspaceId: WorkspaceIdSchema,
  actorUserId: UserIdSchema,
  purpose: ConnectorSessionPurposeSchema,
  threadId: Schema.optionalKey(ThreadIdSchema),
  sessionId: ConnectorProviderSessionIdSchema,
  connections: Schema.Array(ConnectorProviderConnectionProjectionSchema).check(
    Schema.isMaxLength(64),
  ),
  calendarTriggers: Schema.optionalKey(
    Schema.Array(ComposioCalendarTriggerProjectionSchema).check(Schema.isMaxLength(64)),
  ),
});

export const ReconcileConnectorStateResultSchema = Schema.Struct({
  updatedConnections: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
});

export const ConnectorAuthorizationRepositoryErrorDataSchema = Schema.Struct({
  code: Schema.Literals(["INVALID_INPUT", "UNAUTHENTICATED", "FORBIDDEN"]),
  message: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(1_000)),
});

export const decodeConnectorToolkit = Schema.decodeUnknownSync(
  ConnectorToolkitSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeConnectorConnection = Schema.decodeUnknownSync(
  ConnectorConnectionSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeConnectorTool = Schema.decodeUnknownSync(
  ConnectorToolSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeConnectorExecutionRequest = Schema.decodeUnknownSync(
  ConnectorExecutionRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeConnectorExecutionResult = Schema.decodeUnknownSync(
  ConnectorExecutionResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeConnectorAuthorizationRequest = Schema.decodeUnknownSync(
  ConnectorAuthorizationRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeConnectorAuthorizationRequest = Schema.encodeSync(
  ConnectorAuthorizationRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeConnectorAuthorizationResult = Schema.decodeUnknownSync(
  ConnectorAuthorizationResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeConnectorAuthorizationResult = Schema.encodeSync(
  ConnectorAuthorizationResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeConnectorSynchronizationResult = Schema.decodeUnknownSync(
  ConnectorSynchronizationResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeConnectorSynchronizationResult = Schema.encodeSync(
  ConnectorSynchronizationResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeConnectorToolkitKey = Schema.decodeUnknownSync(
  ConnectorToolkitKeySchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeResolveConnectorAuthorizationActorRequest = Schema.decodeUnknownSync(
  ResolveConnectorAuthorizationActorRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeResolveConnectorAuthorizationActorRequest = Schema.encodeSync(
  ResolveConnectorAuthorizationActorRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeResolvedConnectorAuthorizationActor = Schema.decodeUnknownSync(
  ResolvedConnectorAuthorizationActorSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeResolvedConnectorAuthorizationActor = Schema.encodeSync(
  ResolvedConnectorAuthorizationActorSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeResolveConnectorSessionRequest = Schema.decodeUnknownSync(
  ResolveConnectorSessionRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeResolveConnectorSessionRequest = Schema.encodeSync(
  ResolveConnectorSessionRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeResolvedConnectorSession = Schema.decodeUnknownSync(
  ResolvedConnectorSessionSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeResolvedConnectorSession = Schema.encodeSync(
  ResolvedConnectorSessionSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeReconcileConnectorStateRequest = Schema.decodeUnknownSync(
  ReconcileConnectorStateRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeReconcileConnectorStateRequest = Schema.encodeSync(
  ReconcileConnectorStateRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeReconcileConnectorStateResult = Schema.decodeUnknownSync(
  ReconcileConnectorStateResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeReconcileConnectorStateResult = Schema.encodeSync(
  ReconcileConnectorStateResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeComposioConnectionExpiredWebhook = Schema.decodeUnknownSync(
  ComposioConnectionExpiredWebhookSchema,
  ComposioWebhookBoundaryParseOptions,
);
export const decodeComposioCalendarTriggerWebhook = Schema.decodeUnknownSync(
  ComposioCalendarTriggerWebhookSchema,
  ComposioWebhookBoundaryParseOptions,
);
export const decodeComposioWebhook = Schema.decodeUnknownSync(
  ComposioWebhookSchema,
  ComposioWebhookBoundaryParseOptions,
);
export const decodeConnectorAuthorizationRepositoryErrorData = Schema.decodeUnknownSync(
  ConnectorAuthorizationRepositoryErrorDataSchema,
  PapersFlowBoundaryParseOptions,
);

export type ConnectorScope = typeof ConnectorScopeSchema.Type;
export type ConnectorStatus = typeof ConnectorStatusSchema.Type;
export type ToolRisk = typeof ToolRiskSchema.Type;
export type ConnectorToolkit = typeof ConnectorToolkitSchema.Type;
export type ConnectorToolkitEncoded = typeof ConnectorToolkitSchema.Encoded;
export type ConnectorConnection = typeof ConnectorConnectionSchema.Type;
export type ConnectorConnectionEncoded = typeof ConnectorConnectionSchema.Encoded;
export type ConnectorTool = typeof ConnectorToolSchema.Type;
export type ConnectorToolEncoded = typeof ConnectorToolSchema.Encoded;
export type ConnectorExecutionRequest = typeof ConnectorExecutionRequestSchema.Type;
export type ConnectorExecutionRequestEncoded = typeof ConnectorExecutionRequestSchema.Encoded;
export type ConnectorExecutionResult = typeof ConnectorExecutionResultSchema.Type;
export type ConnectorExecutionResultEncoded = typeof ConnectorExecutionResultSchema.Encoded;
export type ConnectorAuthorizationRequest = typeof ConnectorAuthorizationRequestSchema.Type;
export type ConnectorAuthorizationRequestEncoded =
  typeof ConnectorAuthorizationRequestSchema.Encoded;
export type ConnectorAuthorizationResult = typeof ConnectorAuthorizationResultSchema.Type;
export type ConnectorAuthorizationResultEncoded = typeof ConnectorAuthorizationResultSchema.Encoded;
export type ConnectorSynchronizationResult = typeof ConnectorSynchronizationResultSchema.Type;
export type ConnectorSynchronizationResultEncoded =
  typeof ConnectorSynchronizationResultSchema.Encoded;
export type ResolveConnectorAuthorizationActorRequest =
  typeof ResolveConnectorAuthorizationActorRequestSchema.Type;
export type ResolveConnectorAuthorizationActorRequestEncoded =
  typeof ResolveConnectorAuthorizationActorRequestSchema.Encoded;
export type ResolvedConnectorAuthorizationActor =
  typeof ResolvedConnectorAuthorizationActorSchema.Type;
export type ResolvedConnectorAuthorizationActorEncoded =
  typeof ResolvedConnectorAuthorizationActorSchema.Encoded;
export type ConnectorSessionPurpose = typeof ConnectorSessionPurposeSchema.Type;
export type ResolveConnectorSessionRequest = typeof ResolveConnectorSessionRequestSchema.Type;
export type ResolveConnectorSessionRequestEncoded =
  typeof ResolveConnectorSessionRequestSchema.Encoded;
export type ResolvedConnectorSession = typeof ResolvedConnectorSessionSchema.Type;
export type ResolvedConnectorSessionEncoded = typeof ResolvedConnectorSessionSchema.Encoded;
export type ConnectorProviderConnectionStatus = typeof ConnectorProviderConnectionStatusSchema.Type;
export type ConnectorProviderConnectionProjection =
  typeof ConnectorProviderConnectionProjectionSchema.Type;
export type ConnectorProviderConnectionProjectionEncoded =
  typeof ConnectorProviderConnectionProjectionSchema.Encoded;
export type ComposioCalendarTriggerSlug = typeof ComposioCalendarTriggerSlugSchema.Type;
export type ComposioCalendarTriggerProjection = typeof ComposioCalendarTriggerProjectionSchema.Type;
export type ComposioCalendarTriggerProjectionEncoded =
  typeof ComposioCalendarTriggerProjectionSchema.Encoded;
export type ReconcileConnectorStateRequest = typeof ReconcileConnectorStateRequestSchema.Type;
export type ReconcileConnectorStateRequestEncoded =
  typeof ReconcileConnectorStateRequestSchema.Encoded;
export type ReconcileConnectorStateResult = typeof ReconcileConnectorStateResultSchema.Type;
export type ReconcileConnectorStateResultEncoded =
  typeof ReconcileConnectorStateResultSchema.Encoded;
export type ComposioConnectionExpiredWebhook = typeof ComposioConnectionExpiredWebhookSchema.Type;
export type ComposioConnectionExpiredWebhookEncoded =
  typeof ComposioConnectionExpiredWebhookSchema.Encoded;
export type ComposioCalendarTriggerWebhook = typeof ComposioCalendarTriggerWebhookSchema.Type;
export type ComposioCalendarTriggerWebhookEncoded =
  typeof ComposioCalendarTriggerWebhookSchema.Encoded;
export type ComposioWebhook = typeof ComposioWebhookSchema.Type;
export type ComposioWebhookEncoded = typeof ComposioWebhookSchema.Encoded;
export type ConnectorAuthorizationRepositoryErrorData =
  typeof ConnectorAuthorizationRepositoryErrorDataSchema.Type;

export function composioConnectorUserId(input: {
  readonly workspaceId: string;
  readonly userId: string;
}): string {
  return `workspace_${input.workspaceId}_user_${input.userId}`;
}
